import type { NotificationWorkflowItem } from '../../../shared/services/notification/workflow';
import { parseNumeric, postRequest } from '../../../shared/utils';

type Decision = 'ACCEPT' | 'REJECT';

type ResolveResult = {
  handled: boolean;
};

function parseText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isMeToAvNewComponentPayload(payload: Record<string, unknown>): boolean {
  const screen = parseText(payload.screen);
  return screen ? screen.toUpperCase() === 'NPI_STEERING' : true;
}

async function resolveAssignedDoneStateId(screenGridId: number): Promise<number | null> {
  const result = await postRequest('/api/records', {
    columns: ['id', 'state'],
    screenGridId,
    schema: 'npi',
    table: 'tblopt_done_state',
  });

  const rows = result?.data?.npi_tblopt_done_state;
  const safeRows = Array.isArray(rows) ? rows : [];

  const assignedRecord = safeRows.find((row) => {
    const state = parseText((row as { state?: unknown })?.state);
    if (!state) return false;
    const normalizedState = state.trim().toLowerCase();
    return normalizedState === 'assigned' || normalizedState.includes('assigned');
  }) as { id?: unknown; state?: unknown } | undefined;

  const assignedStateId = parseNumeric(assignedRecord?.id);

  return assignedStateId;
}

function buildSheet1AssignPayload(rowId: number, assignedBy: number, erledigtAvStateId: number) {
  return {
    pkey: 'id1',
    schema: 'npi',
    table: 'sheet1',
    deleteIds: [],
    insertObjects: [],
    updateObjects: [
      {
        where: {
          id1: {
            _eq: rowId,
          },
        },
        _set: {
          erledigt_av: erledigtAvStateId,
          planer: assignedBy,
          updated_by: assignedBy,
          update_date: new Date().toISOString().substring(0, 19),
        },
      },
    ],
  };
}

export async function resolveMeToAvNewComponentDecision({
  item,
  decision,
}: {
  item: NotificationWorkflowItem;
  decision: Decision;
}): Promise<ResolveResult> {
  if (decision !== 'ACCEPT') {
    return { handled: false };
  }

  const payload = (item.payload ?? {}) as Record<string, unknown>;

  if (!isMeToAvNewComponentPayload(payload)) {
    return { handled: false };
  }

  const rowId = parseNumeric(payload.rowId);
  const screenGridId = parseNumeric(payload.screenGridId);
  const assignedBy = parseNumeric(item.actingUserId);

  if (rowId == null || screenGridId == null || assignedBy == null) {
    return { handled: false };
  }

  const assignedStateId = await resolveAssignedDoneStateId(screenGridId);

  if (assignedStateId == null) {
    return { handled: false };
  }

  const tables = [buildSheet1AssignPayload(rowId, assignedBy, assignedStateId)];

  const result = await postRequest('/api/tables', {
    screenGridId,
    tables,
  });

  if (result?.errors?.length) {
    throw new Error(
      result.errors[0]?.message ?? 'Failed to execute ME to AV new component accept decision',
    );
  }

  return { handled: true };
}
